import { newIdempotencyKey } from '@micarahr/api-client';
import { apiTransport } from './api-client';

/**
 * Deciding a leave, overtime or correction request, against the API.
 *
 * The screens call one `decide(id, decision, comment)`; the server has a route
 * per outcome. Without this the adapters had no `decide` at all, so every
 * Approve / Reject / Return fell through to the prototype service, which looks
 * in browser storage for a request that only exists on the server and reports
 * it "could not be found".
 *
 * Approval has two routes, `approve-manager` and `approve-hr`. Both land on the
 * same approval engine, which decides whose step is current and refuses anyone
 * it is not — the route only gates on a permission. So the HR route is tried
 * first and the manager route only when the caller lacks the HR permission: a
 * line manager gets the manager route, HR gets the HR route, and nobody can
 * skip a step by choosing a URL.
 */

export type RequestDecision = 'approve' | 'reject' | 'return';

type Resource = 'leave-requests' | 'overtime-requests' | 'attendance-corrections';

export async function decideRequest(
  resource: Resource,
  id: string,
  decision: RequestDecision,
  comment: string,
  extra: Record<string, unknown> = {},
): Promise<unknown> {
  const post = (path: string, body: Record<string, unknown>) =>
    apiTransport().post(`/api/v1/${resource}/${id}/${path}`, body, {
      idempotencyKey: newIdempotencyKey(),
    });

  if (decision !== 'approve') return post(decision, { comment });

  try {
    return await post('approve-hr', { comment, ...extra });
  } catch (error) {
    if ((error as { status?: number }).status !== 403) throw error;
    return post('approve-manager', { comment, ...extra });
  }
}
